import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { rankingsAPI, statsAPI } from '../utils/api';

const LEVEL_LABELS = { world: 'عالمي', continental: 'قاري', regional: 'إقليمي', national: 'وطني', local: 'محلي' };

function RankBadge({ rank }) {
  if (rank === 1) return <div className="rank-badge rank-1">1</div>;
  if (rank === 2) return <div className="rank-badge rank-2">2</div>;
  if (rank === 3) return <div className="rank-badge rank-3">3</div>;
  return <div className="rank-badge rank-other">{rank}</div>;
}

function TrendIcon({ current, previous }) {
  if (!previous || current === previous) return <span className="trend trend-same">—</span>;
  if (current < previous) return <span className="trend trend-up">▲ {previous - current}</span>;
  return <span className="trend trend-down">▼ {current - previous}</span>;
}

function PlayerAvatar({ player }) {
  const initials = (player.full_name || '').split(' ').slice(0, 2).map(w => w[0]).join('');
  return (
    <div className="player-avatar">
      {player.photo_url ? <img src={player.photo_url} alt={player.full_name} /> : initials}
    </div>
  );
}

function Top3Card({ player, rank }) {
  const navigate = useNavigate();
  const crowns = { 1: '👑', 2: '🥈', 3: '🥉' };
  const medalClass = { 1: 'gold', 2: 'silver', 3: 'bronze' };
  const initials = (player.full_name || '').split(' ').slice(0, 2).map(w => w[0]).join('');

  return (
    <div className={`top3-card rank-${rank}`} onClick={() => navigate(`/players/${player.id}`)} style={{ cursor: 'pointer' }}>
      <div className="top3-crown">{crowns[rank]}</div>
      <div className={`top3-avatar ${medalClass[rank]}`}>
        {player.photo_url ? <img src={player.photo_url} alt={player.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
      </div>
      <div className="top3-name">{player.full_name_ar || player.full_name}</div>
      <div className="top3-country">{player.flag_emoji} {player.country_name_ar || player.country_name}</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        {player.sport_icon && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{player.sport_icon} {player.sport_name_ar || player.sport_name}</span>}
      </div>
      <div className="top3-points">{Math.round(player.total_points).toLocaleString()}</div>
      <div className="top3-points-label">نقطة</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--gold)' }}>🥇 {player.gold_medals}</span>
        <span style={{ fontSize: 12, color: 'var(--silver)' }}>🥈 {player.silver_medals}</span>
        <span style={{ fontSize: 12, color: 'var(--bronze)' }}>🥉 {player.bronze_medals}</span>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ sport_id: '', country_id: '', page: 1 });

  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ['leaderboard', filters],
    queryFn: () => rankingsAPI.getLeaderboard({ ...filters, limit: 25 }).then(r => r.data)
  });

  const { data: sports } = useQuery({ queryKey: ['sports'], queryFn: () => statsAPI.sports().then(r => r.data) });
  const { data: countries } = useQuery({ queryKey: ['countries'], queryFn: () => statsAPI.countries().then(r => r.data) });

  const players = leaderboard?.data || [];
  const top3 = players.slice(0, 3);
  const rest = players.slice(3);

  return (
    <div>
      <div className="topbar">
        <div>
          <div className="topbar-title">🏆 لوحة التصنيف العالمي</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Global Sports Leaderboard</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {leaderboard?.total ? `${leaderboard.total} لاعب مصنف` : ''}
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="filter-bar">
          <div className="search-bar" style={{ flex: 1, maxWidth: 300 }}>
            <input placeholder="🔍 بحث عن لاعب..." onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
          </div>
          <select value={filters.sport_id} onChange={e => setFilters(f => ({ ...f, sport_id: e.target.value, page: 1 }))}>
            <option value="">🏅 جميع الرياضات</option>
            {sports?.data?.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name_ar || s.name}</option>)}
          </select>
          <select value={filters.country_id} onChange={e => setFilters(f => ({ ...f, country_id: e.target.value, page: 1 }))}>
            <option value="">🌍 جميع الدول</option>
            {countries?.data?.map(c => <option key={c.id} value={c.id}>{c.flag_emoji} {c.name_ar || c.name}</option>)}
          </select>
        </div>

        {/* Top 3 Podium */}
        {!filters.sport_id && !filters.country_id && top3.length === 3 && (
          <div className="top3-grid">
            <Top3Card player={top3[1]} rank={2} />
            <Top3Card player={top3[0]} rank={1} />
            <Top3Card player={top3[2]} rank={3} />
          </div>
        )}

        {/* Rankings Table */}
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            {isLoading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : players.length === 0 ? (
              <div className="empty"><div className="empty-icon">🏆</div><p>لا توجد بيانات تصنيف حتى الآن</p></div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>اللاعب</th>
                    <th>الدولة</th>
                    <th>الرياضة</th>
                    <th>النقاط</th>
                    <th>المشاركات</th>
                    <th>🥇</th>
                    <th>🥈</th>
                    <th>🥉</th>
                    <th>التغيير</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map(p => (
                    <tr key={p.id} onClick={() => navigate(`/players/${p.id}`)}>
                      <td><RankBadge rank={p.rank_position} /></td>
                      <td>
                        <div className="player-cell">
                          <PlayerAvatar player={p} />
                          <div className="player-info">
                            <div className="name">{p.full_name_ar || p.full_name}</div>
                            {p.full_name_ar && <div className="name-ar">{p.full_name}</div>}
                          </div>
                        </div>
                      </td>
                      <td>{p.flag_emoji} {p.country_name_ar || p.country_name || '—'}</td>
                      <td>{p.sport_icon} {p.sport_name_ar || p.sport_name || '—'}</td>
                      <td><span className="points-value">{Math.round(p.total_points).toLocaleString()}</span></td>
                      <td style={{ color: 'var(--text-secondary)' }}>{p.total_participations}</td>
                      <td style={{ color: 'var(--gold)', fontWeight: 700 }}>{p.gold_medals}</td>
                      <td style={{ color: 'var(--silver)', fontWeight: 700 }}>{p.silver_medals}</td>
                      <td style={{ color: 'var(--bronze)', fontWeight: 700 }}>{p.bronze_medals}</td>
                      <td><TrendIcon current={p.rank_position} previous={p.previous_rank} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {leaderboard?.pages > 1 && (
            <div className="pagination" style={{ padding: '16px 0' }}>
              {Array.from({ length: leaderboard.pages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === filters.page ? 'active' : ''}`}
                  onClick={() => setFilters(f => ({ ...f, page: p }))}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
